import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { courseCategories, courses, getCoursesByCategory } from "@/data/courses";
import { BookOpen, ChevronRight, GraduationCap, Landmark, Shield, Train, ShieldCheck, FileText } from "lucide-react";
import { useState, useEffect } from "react";

const iconMap = { Landmark, Shield, Train, GraduationCap, ShieldCheck, FileText };

export default function CoursesPage() {
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get("category");
  const [activeCategory, setActiveCategory] = useState(categoryParam || "all");

  useEffect(() => {
    if (categoryParam) setActiveCategory(categoryParam);
  }, [categoryParam]);

  const filteredCourses = activeCategory === "all" ? courses : getCoursesByCategory(activeCategory);

  return (
    <div data-testid="courses-page">
      <Helmet>
        <title>Banking, SSC, MBA & Railway Courses | Vedanti</title>
        <meta name="description" content="Explore 30+ competitive exam courses at Vedanti Academy - IBPS PO/Clerk, SBI, RBI, SSC CGL/CHSL/MTS, Railway NTPC, MBA CET, SNAP, CMAT, LIC, GIC, NIACL and more. Enroll today!" />
        <meta name="keywords" content="banking exam courses, SSC coaching courses, MBA CET preparation, railway exam coaching, IBPS PO course, SBI clerk course, competitive exam classes" />
        <meta property="og:title" content="30+ Competitive Exam Courses | Vedanti Academy" />
        <meta property="og:description" content="Complete coaching for Banking, SSC, MBA, Railway & Insurance exams. Updated syllabus, mock tests & expert guidance." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://vedantiacademy.com/courses" />
        <link rel="canonical" href="https://vedantiacademy.com/courses" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Vedanti Academy Courses",
          "description": "Comprehensive coaching for 30+ competitive exams including Banking, SSC, MBA, Railway and Insurance.",
          "url": "https://vedantiacademy.com/courses",
          "numberOfItems": 32,
          "itemListElement": [
            {"@type": "ListItem", "position": 1, "name": "IBPS Clerk Coaching", "url": "https://vedantiacademy.com/courses/ibps-clerk"},
            {"@type": "ListItem", "position": 2, "name": "IBPS PO Coaching", "url": "https://vedantiacademy.com/courses/ibps-po"},
            {"@type": "ListItem", "position": 3, "name": "SBI Clerk Coaching", "url": "https://vedantiacademy.com/courses/sbi-clerk"},
            {"@type": "ListItem", "position": 4, "name": "SBI PO Coaching", "url": "https://vedantiacademy.com/courses/sbi-po"},
            {"@type": "ListItem", "position": 5, "name": "SSC CGL Coaching", "url": "https://vedantiacademy.com/courses/ssc-cgl"},
            {"@type": "ListItem", "position": 6, "name": "MAH MBA CET Coaching", "url": "https://vedantiacademy.com/courses/mah-mba-cet"},
            {"@type": "ListItem", "position": 7, "name": "Railway NTPC Coaching", "url": "https://vedantiacademy.com/courses/railway-ntpc"},
            {"@type": "ListItem", "position": 8, "name": "LIC Assistant Coaching", "url": "https://vedantiacademy.com/courses/lic-assistant"}
          ]
        })}</script>
      </Helmet>
      <section className="bg-vedanti-navy py-12 md:py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(249,115,22,0.15)_0%,_transparent_60%)]"></div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-vedanti-orange via-vedanti-mango to-vedanti-orange"></div>
        <div className="container-main relative z-10">
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-white">Our <span className="text-vedanti-orange">Courses</span></h1>
          <p className="text-slate-300 mt-3 text-lg">Comprehensive coaching for 30+ competitive exams</p>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-main">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-10" data-testid="course-filter">
            <button
              onClick={() => setActiveCategory("all")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeCategory === "all"
                  ? "bg-vedanti-navy text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid="filter-all"
            >
              All Courses ({courses.length})
            </button>
            {courseCategories.map((cat) => {
              const count = getCoursesByCategory(cat.id).length;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeCategory === cat.id
                      ? "bg-vedanti-navy text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  data-testid={`filter-${cat.id}`}
                >
                  {cat.name} ({count})
                </button>
              );
            })}
          </div>

          {/* Course Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses.map((course) => {
              const category = courseCategories.find((c) => c.id === course.category);
              return (
                <Link
                  key={course.slug}
                  to={`/courses/${course.slug}`}
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg transition-all p-6 group card-hover"
                  data-testid={`course-card-${course.slug}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                      course.category === "banking" ? "bg-vedanti-navy/10 text-vedanti-navy" :
                      course.category === "ssc" ? "bg-vedanti-orange/10 text-vedanti-orange" :
                      course.category === "railway" ? "bg-blue-100 text-blue-700" :
                      course.category === "mba" ? "bg-vedanti-mango/10 text-amber-700" :
                      course.category === "insurance" ? "bg-green-100 text-green-700" :
                      "bg-slate-100 text-slate-600"
                    }`}>
                      {category?.name}
                    </span>
                  </div>
                  <h3 className="font-heading font-bold text-lg text-vedanti-navy mb-2">{course.name}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed mb-3 line-clamp-2">{course.description}</p>
                  <p className="text-xs text-slate-400 mb-4">{course.eligibility}</p>
                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <span className="text-xs text-slate-400">{course.batchInfo?.split("|")[0]}</span>
                    <span className="text-vedanti-orange font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                      Details <ChevronRight size={14} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

          {filteredCourses.length === 0 && (
            <div className="text-center py-16">
              <BookOpen size={48} className="text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No courses found in this category.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
